import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Upload, Download, Loader2, Eye,
  CheckCircle2, Sparkles, X, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import * as faceapi from "face-api.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// face-api.js CDN weights (public, no key required)
const FACEAPI_MODEL_URL =
  "https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights";

const CAT_COLORS = {
  personality:   "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  emotional:     "bg-rose-500/15 text-rose-300 border-rose-400/40",
  mental:        "bg-indigo-500/15 text-indigo-300 border-indigo-400/40",
  social:        "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  relationships: "bg-pink-500/15 text-pink-300 border-pink-400/40",
  career:        "bg-blue-500/15 text-blue-300 border-blue-400/40",
  creativity:    "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/40",
  intelligence:  "bg-cyan-500/15 text-cyan-300 border-cyan-400/40",
  health:        "bg-red-500/15 text-red-300 border-red-400/40",
  morality:      "bg-amber-500/15 text-amber-300 border-amber-400/40",
  physical:      "bg-teal-500/15 text-teal-300 border-teal-400/40",
  other:         "bg-neutral-500/15 text-neutral-300 border-neutral-400/40",
};

const CATEGORY_ORDER = [
  "personality", "emotional", "mental", "social", "relationships",
  "career", "creativity", "intelligence", "physical", "morality", "health", "other",
];

/**
 * Generic checklist-based analysis page (palmistry, face reading).
 * Copies the working handwriting UX 1:1 but without OCR.
 */
export default function ChecklistAnalysisPage({
  moduleKey,           // "palmistry" | "facereading"
  title,               // "Palmistry" | "Face Reading"
  subtitle,            // "based on Cheiro" | "based on Mary O. Stanton"
  pageIcon: Icon,      // lucide icon component
  uploadHint,          // "photo of your palm"
  subjectLabel = "Subject",
  pdfBasename,         // "palmistry-report"
  testidPrefix,        // "palm" | "face"
}) {
  const [groups, setGroups] = useState([]);
  const [multiKeys, setMultiKeys] = useState([]);
  const [selected, setSelected] = useState({});
  const [subjectName, setSubjectName] = useState("");
  const [notes, setNotes] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const fileRef = useRef(null);

  // --- face-api.js state (only used when moduleKey === "facereading") ---
  const isFace = moduleKey === "facereading";
  const [faceModelsReady, setFaceModelsReady] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [autoResult, setAutoResult] = useState(null); // {shape, matched:[{key,label}], gender, age, expression}

  // Pre-load face-api.js models once when the page mounts (only for face-reading)
  useEffect(() => {
    if (!isFace) return;
    let cancelled = false;
    (async () => {
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(FACEAPI_MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(FACEAPI_MODEL_URL),
          faceapi.nets.ageGenderNet.loadFromUri(FACEAPI_MODEL_URL),
          faceapi.nets.faceExpressionNet.loadFromUri(FACEAPI_MODEL_URL),
        ]);
        if (!cancelled) setFaceModelsReady(true);
      } catch (e) {
        console.warn("face-api models failed to load:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [isFace]);

  // Classify face shape from 68 landmarks (jaw+face height ratio)
  const classifyFaceShape = (landmarks) => {
    const pts = landmarks.positions;
    // Jaw outer points: 0 (left ear) → 16 (right ear)
    const jawL = pts[0], jawR = pts[16];
    const chin = pts[8];
    const foreheadCenter = pts[27]; // top of nose bridge as forehead-ish anchor
    // Cheekbone width (2 → 14)
    const cheekL = pts[2], cheekR = pts[14];
    // Forehead approximation: mid of eyebrows (17 → 26) span
    const browL = pts[17], browR = pts[26];

    const jawWidth = Math.hypot(jawL.x - jawR.x, jawL.y - jawR.y);
    const cheekWidth = Math.hypot(cheekL.x - cheekR.x, cheekL.y - cheekR.y);
    const browWidth = Math.hypot(browL.x - browR.x, browL.y - browR.y);
    const faceHeight = Math.hypot(foreheadCenter.x - chin.x, foreheadCenter.y - chin.y) * 1.35;

    const wOverH = cheekWidth / faceHeight;                 // ~1 = round, <0.75 = long
    const jawOverCheek = jawWidth / cheekWidth;             // >0.95 = square, <0.75 = heart/pyriform
    const browOverJaw = browWidth / jawWidth;               // >1.05 = dome (broad forehead)

    // Match against selection keys the backend rules expose
    const matched = [];
    let shape = "oval";
    if (jawOverCheek >= 0.94 && wOverH >= 0.85) {
      shape = "square"; matched.push({ key: "face_shape.square", label: "Square face" });
    } else if (wOverH >= 0.90) {
      shape = "round"; matched.push({ key: "face_shape.round", label: "Round face" });
    } else if (jawOverCheek <= 0.78) {
      shape = "pyriform (pear)"; matched.push({ key: "face_shape.pyriform", label: "Pear-shaped head" });
    } else if (browOverJaw >= 1.05) {
      shape = "dome"; matched.push({ key: "face_shape.dome", label: "Broad, dome-shaped head" });
    } else {
      shape = "oval / balanced";
    }
    return { shape, matched, metrics: { wOverH, jawOverCheek, browOverJaw } };
  };

  // Run detection on the currently-loaded preview
  const runFaceDetection = useCallback(async () => {
    if (!isFace || !imagePreview || !faceModelsReady) return;
    setAutoDetecting(true); setAutoResult(null);
    try {
      const imgEl = new window.Image();
      imgEl.src = imagePreview;
      await new Promise((res, rej) => { imgEl.onload = res; imgEl.onerror = rej; });

      const det = await faceapi
        .detectSingleFace(imgEl, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 }))
        .withFaceLandmarks()
        .withAgeAndGender()
        .withFaceExpressions();

      if (!det) {
        toast.error("No face detected. Try a clearer front-facing photo.");
        setAutoDetecting(false); return;
      }

      const { shape, matched, metrics } = classifyFaceShape(det.landmarks);
      const topExpression = Object.entries(det.expressions || {})
        .sort((a, b) => b[1] - a[1])[0]?.[0];

      // Auto-check the matched selection keys
      setSelected((s) => {
        const next = { ...s };
        // Reset radio group first
        for (const k of Object.keys(next)) if (k.startsWith("face_shape.")) delete next[k];
        for (const m of matched) next[m.key] = true;
        return next;
      });

      setAutoResult({
        shape, matched, metrics,
        age: Math.round(det.age || 0),
        gender: det.gender,
        expression: topExpression,
      });
      toast.success(`Face detected → ${shape}. ${matched.length} rule(s) auto-checked.`);
    } catch (e) {
      console.error(e);
      toast.error("Auto-detect failed. You can still tick features manually.");
    } finally { setAutoDetecting(false); }
  }, [isFace, imagePreview, faceModelsReady]);

  // Auto-run detection whenever a new photo is loaded (face-reading only)
  useEffect(() => {
    if (isFace && imagePreview && faceModelsReady) runFaceDetection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePreview, faceModelsReady, isFace]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/${moduleKey}/rules`);
        setGroups(data.groups || []);
        setMultiKeys(data.multi_select || []);
      } catch (e) {
        toast.error(`Failed to load ${title.toLowerCase()} ruleset`);
      }
    })();
  }, [moduleKey, title]);

  const totalSelected = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setImageFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(f);
  };
  const clearImage = () => {
    setImageFile(null); setImagePreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const toggle = useCallback((groupKey, optionKey, isRadio) => {
    setSelected((s) => {
      const next = { ...s };
      if (isRadio) {
        for (const k of Object.keys(next)) if (k.startsWith(groupKey + ".")) delete next[k];
        next[optionKey] = true;
      } else {
        if (next[optionKey]) delete next[optionKey]; else next[optionKey] = true;
      }
      return next;
    });
  }, []);

  const clearAll = () => { setSelected({}); setResult(null); };

  const multiSet = useMemo(() => new Set(multiKeys), [multiKeys]);

  const analyze = async () => {
    if (totalSelected === 0) { toast.error("Select at least one feature"); return; }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("selections_json", JSON.stringify({
        selection_keys: Object.keys(selected),
        subject_name: subjectName,
        notes,
      }));
      if (imageFile) fd.append("image", imageFile);
      const { data } = await axios.post(`${API}/${moduleKey}/analyze`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setTimeout(() => {
        document.getElementById("chk-results")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      toast.success(`${title} — ${data.matched_rules.length} traits interpreted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  const downloadPdf = async () => {
    if (!result) return;
    setDownloadingPdf(true);
    try {
      const payload = {
        subject_name: result.subject_name || "",
        notes: result.notes || "",
        matched_rules: result.matched_rules || [],
        categories: result.categories || {},
        summary: result.summary || "",
      };
      const r = await axios.post(`${API}/${moduleKey}/report.pdf`, payload,
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pdfBasename}-${(subjectName || "report").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
    finally { setDownloadingPdf(false); }
  };

  const orderedCats = useMemo(() => {
    if (!result?.categories) return [];
    const keys = Object.keys(result.categories);
    return [
      ...CATEGORY_ORDER.filter((c) => keys.includes(c)),
      ...keys.filter((c) => !CATEGORY_ORDER.includes(c)),
    ];
  }, [result]);

  const totalOptions = groups.reduce((n, g) => n + g.options.length, 0);

  return (
    <div className="min-h-screen bg-[#0F1115] text-[#E6E1D6]">
      <div className="grain-overlay pointer-events-none fixed inset-0 z-0" />

      <header className="relative z-10 border-b border-[#2A2D35] bg-[#0F1115]/90 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/"
                  className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono
                             text-[#A19E95] hover:text-[#D4AF37] transition-colors"
                  data-testid={`${testidPrefix}-back-link`}>
              <ArrowLeft size={12} /> Dashboard
            </Link>
            <div className="h-6 w-px bg-[#2A2D35]" />
            <div className="min-w-0">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.4em] text-[#6E6B64] font-mono">
                Astrology Studio
              </div>
              <div className="font-heading text-lg sm:text-xl text-[#E6E1D6] flex items-center gap-2">
                {Icon && <Icon size={18} className="text-[#D4AF37]" />}
                {title}
              </div>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-6 border border-[#2A2D35] bg-[#151821] rounded-sm px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-start gap-3">
            <Sparkles className="text-[#D4AF37] shrink-0 mt-0.5" size={16} />
            <div className="text-xs sm:text-sm text-[#A19E95] leading-relaxed">
              Upload {uploadHint} for reference and tick the observed features below.
              We match your selections against a curated set of{" "}
              <span className="text-[#D4AF37]">{totalOptions} rules</span> {subtitle} and give you
              a full categorised interpretation.
              <span className="block mt-1 text-[#6E6B64] italic">100% free · zero AI credits used.</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64] mb-3">
                Reference photo (optional)
              </div>
              {imagePreview ? (
                <div className="relative">
                  <img src={imagePreview} alt="preview"
                       className="w-full rounded-sm border border-[#2A2D35]"
                       data-testid={`${testidPrefix}-image-preview`} />
                  <button onClick={clearImage}
                          className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1"
                          data-testid={`${testidPrefix}-clear-image-btn`}>
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()}
                        className="w-full h-40 border-2 border-dashed border-[#2A2D35] hover:border-[#D4AF37]/50
                                   rounded-sm flex flex-col items-center justify-center gap-2 text-[#6E6B64]
                                   hover:text-[#D4AF37] transition-colors"
                        data-testid={`${testidPrefix}-upload-dropzone`}>
                  <Upload size={22} />
                  <span className="text-xs">Click to upload {uploadHint}</span>
                  <span className="text-[10px] font-mono">JPG · PNG · optional</span>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*"
                     onChange={onPickFile} className="hidden"
                     data-testid={`${testidPrefix}-file-input`} />
            </div>

            {/* --- Face-api.js auto-detection panel (face-reading only) --- */}
            {isFace && imagePreview && (
              <div className="border border-[#D4AF37]/30 bg-[#181A1F] rounded-sm p-4 space-y-2"
                   data-testid="face-autodetect-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wand2 size={14} className="text-[#D4AF37]" />
                    <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#D4AF37]">
                      Auto-detect (in-browser, free)
                    </span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={runFaceDetection}
                          disabled={!faceModelsReady || autoDetecting}
                          data-testid="face-autodetect-btn"
                          className="text-[10px] uppercase tracking-widest font-mono text-[#A19E95] hover:text-[#D4AF37]">
                    {autoDetecting ? <Loader2 size={12} className="animate-spin" />
                                   : (faceModelsReady ? "Re-detect" : "Loading…")}
                  </Button>
                </div>
                {autoResult ? (
                  <div className="text-xs text-[#C7C2B6] space-y-1 font-mono">
                    <div>Face shape: <span className="text-[#D4AF37]">{autoResult.shape}</span></div>
                    {autoResult.matched.length > 0 && (
                      <div className="text-[10px] text-emerald-300">
                        Auto-checked: {autoResult.matched.map((m) => m.label).join(" · ")}
                      </div>
                    )}
                    <div className="text-[10px] text-[#6E6B64]">
                      Age ≈ {autoResult.age} · {autoResult.gender} · mood: {autoResult.expression}
                    </div>
                  </div>
                ) : (
                  <div className="text-[10px] font-mono text-[#6E6B64] italic">
                    {faceModelsReady
                      ? "Ready — analysing photo…"
                      : "Loading vision models from CDN (first time only)…"}
                  </div>
                )}
              </div>
            )}

            <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4 space-y-3">
              <div>
                <Label htmlFor="subject" className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64]">
                  {subjectLabel} (optional)
                </Label>
                <Input id="subject" value={subjectName}
                       onChange={(e) => setSubjectName(e.target.value)}
                       className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]"
                       placeholder="e.g. Ahalya"
                       data-testid={`${testidPrefix}-subject-name`} />
              </div>
              <div>
                <Label htmlFor="notes" className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64]">
                  Analyst notes (optional)
                </Label>
                <Textarea id="notes" value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          className="mt-1 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] min-h-[80px]"
                          placeholder="Anything noteworthy about the subject or the sample…"
                          data-testid={`${testidPrefix}-notes`} />
              </div>
            </div>

            <div className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4 sticky top-24 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#6E6B64]">
                  Selections
                </div>
                <div className="text-lg font-heading text-[#D4AF37]"
                     data-testid={`${testidPrefix}-selection-count`}>{totalSelected}</div>
              </div>
              <div className="flex gap-2">
                <Button onClick={analyze} disabled={analyzing}
                        className="flex-1 bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158] font-semibold"
                        data-testid={`${testidPrefix}-analyze-btn`}>
                  {analyzing ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Analyzing…</>)
                             : (<><Eye size={14} className="mr-2" /> Analyze</>)}
                </Button>
                <Button onClick={clearAll} variant="outline"
                        className="border-[#2A2D35] text-[#A19E95] hover:text-[#D4AF37]"
                        data-testid={`${testidPrefix}-clear-btn`}>Reset</Button>
              </div>
              {result && (
                <Button onClick={downloadPdf} disabled={downloadingPdf} variant="outline"
                        className="w-full border-[#2A2D35] text-[#E6E1D6] hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
                        data-testid={`${testidPrefix}-pdf-btn`}>
                  {downloadingPdf ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Building PDF…</>)
                                  : (<><Download size={14} className="mr-2" /> Download PDF report</>)}
                </Button>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            {groups.map((g) => {
              const isMulti = multiSet.has(g.key);
              return (
                <div key={g.key}
                     className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4"
                     data-testid={`${testidPrefix}-group-${g.key}`}>
                  <div className="mb-2">
                    <div className="text-sm font-heading text-[#E6E1D6]">{g.label}</div>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-[#6E6B64]">
                      {isMulti ? "Multi-select" : "Choose one"} · {g.options.length} options
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {g.options.map((opt) => {
                      const active = !!selected[opt.selection_key];
                      return (
                        <button key={opt.selection_key}
                                onClick={() => toggle(g.key, opt.selection_key, !isMulti)}
                                className={`text-left px-3 py-2 rounded-sm border transition-colors flex items-start gap-2
                                  ${active
                                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E6E1D6]"
                                    : "border-[#2A2D35] bg-[#0F1115] text-[#A19E95] hover:border-[#D4AF37]/40 hover:text-[#E6E1D6]"
                                  }`}
                                data-testid={`${testidPrefix}-option-${opt.selection_key}`}>
                          <span className={`mt-0.5 inline-block w-3 h-3 shrink-0 border ${
                                  active ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#2A2D35]"
                                } ${isMulti ? "" : "rounded-full"}`}>
                            {active && <CheckCircle2 size={12} className="text-[#0F1115] -m-px" />}
                          </span>
                          <span className="text-xs leading-tight">{opt.sign}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {result && (
          <div id="chk-results" className="mt-10 space-y-4">
            <div className="border border-[#D4AF37]/40 bg-[#151821] rounded-sm p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#D4AF37] mb-1">
                Analysis · {result.matched_rules.length} traits
              </div>
              <div className="text-sm text-[#E6E1D6] leading-relaxed"
                   data-testid={`${testidPrefix}-summary`}>{result.summary}</div>
            </div>

            {orderedCats.map((cat) => {
              const rules = result.categories[cat] || [];
              const badge = CAT_COLORS[cat] || CAT_COLORS.other;
              return (
                <div key={cat}
                     className="border border-[#2A2D35] bg-[#151821] rounded-sm p-4"
                     data-testid={`${testidPrefix}-category-${cat}`}>
                  <div className={`inline-block text-[10px] uppercase tracking-[0.3em] font-mono
                                   px-2 py-1 border rounded-sm mb-3 ${badge}`}>
                    {cat} · {rules.length}
                  </div>
                  <div className="space-y-3">
                    {rules.map((r, i) => (
                      <div key={r.selection_key + "_" + i} className="border-l-2 border-[#2A2D35] pl-3">
                        <div className="text-xs uppercase tracking-widest font-mono text-[#6E6B64] mb-0.5">
                          {r.feature.replace(/_/g, " ")}
                        </div>
                        <div className="text-sm text-[#E6E1D6] font-medium">{r.sign}</div>
                        <div className="text-xs text-[#A19E95] mt-1 leading-relaxed">{r.meaning}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
